import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleInfo, faTriangleExclamation, faXmark } from '@fortawesome/free-solid-svg-icons';
import { serviceSlugForPath } from '../../config/serviceAccess';
import paymentService from '../../services/paymentService';
import PayURedirectForm, { PayURedirectRequest, usePayUCheckout } from './PayURedirectForm';
import {
  WalletProvider,
  WalletCheckoutMeta,
  clearPendingCheckout,
  isWalletHidden,
  readPendingCheckout,
  savePendingCheckout,
  walletProviderForSlug,
} from './walletProviders';
import './ServiceWallet.css';

export type WalletPaymentStatus = 'success' | 'failed' | 'pending' | 'unknown';

export interface WalletPaymentResult {
  status: WalletPaymentStatus;
  tone: 'success' | 'error' | 'info';
  message: string;
}

interface ServiceWalletContextValue {
  provider: WalletProvider;
  balance: number | null;
  loading: boolean;
  hidden: boolean;
  returnPath: string;
  paymentResult: WalletPaymentResult | null;
  dismissPaymentResult: () => void;
  refresh: () => void;
  openCredits: () => void;
}

const noop = () => {};

const defaultValue: ServiceWalletContextValue = {
  provider: walletProviderForSlug(undefined),
  balance: null,
  loading: false,
  hidden: false,
  returnPath: '/',
  paymentResult: null,
  dismissPaymentResult: noop,
  refresh: noop,
  openCredits: noop,
};

const ServiceWalletContext = createContext<ServiceWalletContextValue>(defaultValue);

export const useServiceWallet = () => useContext(ServiceWalletContext);

const PAYMENT_PARAMS = ['payment', 'txnid', 'txn_id', 'mihpayid', 'status'];

const searchWithoutPaymentParams = (search: string): string => {
  const params = new URLSearchParams(search);
  PAYMENT_PARAMS.forEach((key) => params.delete(key));
  return params.toString();
};

const pathWithoutPaymentParams = (pathname: string, search: string): string => {
  const remaining = searchWithoutPaymentParams(search);
  return remaining ? `${pathname}?${remaining}` : pathname;
};

export const ServiceWalletProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('services');
  const hidden = isWalletHidden(location.pathname);
  const provider = useMemo(
    () => walletProviderForSlug(serviceSlugForPath(location.pathname)),
    [location.pathname],
  );
  const returnPath = useMemo(
    () => pathWithoutPaymentParams(location.pathname, location.search),
    [location.pathname, location.search],
  );

  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentResult, setPaymentResult] = useState<WalletPaymentResult | null>(null);
  const checkout = usePayUCheckout();

  useEffect(() => {
    setDialogOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (hidden) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    provider
      .fetchBalance()
      .then((value) => {
        if (!cancelled) setBalance(value);
      })
      .catch(() => {
        if (!cancelled) setBalance(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [provider, hidden, refreshToken]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reported = params.get('payment');
    if (!reported) return;

    let cancelled = false;
    const stored = readPendingCheckout();
    const txnId = params.get('txnid') || params.get('txn_id') || stored?.txnId || '';

    const settle = async () => {
      if (reported !== 'success') {
        clearPendingCheckout();
        if (!cancelled) {
          setPaymentResult({
            status: 'failed',
            tone: 'error',
            message: t('wallet.checkoutFailed'),
          });
        }
        return;
      }

      if (!txnId) {
        if (!cancelled) {
          setPaymentResult({
            status: 'unknown',
            tone: 'info',
            message: t('wallet.checkoutUnknown'),
          });
        }
        return;
      }

      try {
        const verification = await paymentService.verifyTransaction(txnId);
        if (cancelled) return;
        if (verification.status === 'success') {
          clearPendingCheckout();
          setPaymentResult({
            status: 'success',
            tone: 'success',
            message: t('wallet.checkoutSuccessToast'),
          });
          setRefreshToken((token) => token + 1);
        } else if (verification.status === 'pending') {
          setPaymentResult({
            status: 'pending',
            tone: 'info',
            message: t('wallet.checkoutPending'),
          });
        } else if (verification.status === 'refunded') {
          clearPendingCheckout();
          setPaymentResult({
            status: 'success',
            tone: 'success',
            message: t('wallet.checkoutSuccessToast'),
          });
          setRefreshToken((token) => token + 1);
        } else {
          clearPendingCheckout();
          setPaymentResult({
            status: 'failed',
            tone: 'error',
            message: t('wallet.checkoutFailed'),
          });
        }
      } catch {
        if (!cancelled) {
          setPaymentResult({
            status: 'unknown',
            tone: 'info',
            message: t('wallet.checkoutUnknown'),
          });
        }
      }
    };

    void settle();
    navigate(
      { pathname: location.pathname, search: searchWithoutPaymentParams(location.search) },
      { replace: true },
    );

    return () => {
      cancelled = true;
    };
  }, [location.search, location.pathname, navigate, t]);

  useEffect(() => {
    if (!paymentResult || paymentResult.status === 'pending' || paymentResult.status === 'unknown') {
      return;
    }
    const timer = window.setTimeout(() => setPaymentResult(null), 9000);
    return () => window.clearTimeout(timer);
  }, [paymentResult]);

  const refresh = useCallback(() => setRefreshToken((token) => token + 1), []);
  const openCredits = useCallback(() => setDialogOpen(true), []);
  const closeCredits = useCallback(() => setDialogOpen(false), []);
  const dismissPaymentResult = useCallback(() => setPaymentResult(null), []);

  const beginCheckout = useCallback(
    (request: PayURedirectRequest, meta: WalletCheckoutMeta) => {
      savePendingCheckout({
        ...meta,
        returnPath,
        startedAt: new Date().toISOString(),
      });
      setDialogOpen(false);
      checkout.begin(request);
    },
    [checkout, returnPath],
  );

  const value = useMemo(
    () => ({
      provider,
      balance,
      loading,
      hidden,
      returnPath,
      paymentResult,
      dismissPaymentResult,
      refresh,
      openCredits,
    }),
    [
      provider,
      balance,
      loading,
      hidden,
      returnPath,
      paymentResult,
      dismissPaymentResult,
      refresh,
      openCredits,
    ],
  );

  const Dialog = provider.Dialog;

  return (
    <ServiceWalletContext.Provider value={value}>
      {children}
      <Dialog
        open={dialogOpen}
        onClose={closeCredits}
        onCheckout={beginCheckout}
        returnPath={returnPath}
        balance={balance}
        onSettled={refresh}
      />
      {paymentResult && (
        <div
          className={`sw-payment-toast sw-payment-toast--${paymentResult.tone}`}
          role="status"
          aria-live="polite"
        >
          <FontAwesomeIcon
            icon={
              paymentResult.tone === 'success'
                ? faCircleCheck
                : paymentResult.tone === 'error'
                ? faTriangleExclamation
                : faCircleInfo
            }
          />
          <span>{paymentResult.message}</span>
          <button
            type="button"
            className="sw-payment-toast-close"
            onClick={dismissPaymentResult}
            aria-label={t('wallet.dismiss')}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}
      <PayURedirectForm checkout={checkout} />
    </ServiceWalletContext.Provider>
  );
};
